import React from 'react';
import PropTypes from 'prop-types';
import { intlShape, injectIntl, FormattedMessage } from 'react-intl';
import withStyles from 'isomorphic-style-loader/withStyles';
import classNames from 'classnames';
import _ from 'lodash';
import objectHash from 'object-hash';

import { withStyles as muiWithStyles } from '@material-ui/core';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import IconButton from '@material-ui/core/IconButton';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import Fab from '@material-ui/core/Fab';
import Button from '@material-ui/core/Button';

import withWidth, { isWidthUp } from '@material-ui/core/withWidth';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as tourActions from '../../../actions/tours';
import * as lessonActions from '../../../actions/lessonDetails';

import Link from '../../../components/Link';
import CustomModal from '../../../components/Modals/Modal';
import SectionItemNavigationMenu from '../../../components/SectionItemNavigationMenu/SectionItemNavigationMenu';
import HelpButton from '../../../components/Navigation/HelpButton/HelpButton';
import Tour from '../../../components/Tour';
import { isTourShown } from '../../../helpers';
import isMobileUserAgent from '../../../helpers/checkMobileUserAgent';
import LessonChangelog from '../LessonChangelog/LessonChangelog';
import CourseReviewForm from '../CourseReviewForm/CourseReviewForm';
import { messages as tourMessages } from '../Tour';
import SkholeIcon36 from '../../../components/statelessComponents/Icons/SkholeIcon36';

import messages from '../lessonDetailsMessages';
import s from './LessonHeader.scss';
import SkholeIcon19 from '../../../components/statelessComponents/Icons/SkholeIcon19';
import SkholeIcon20 from '../../../components/statelessComponents/Icons/SkholeIcon20';
import SkholeIcon18 from '../../../components/statelessComponents/Icons/SkholeIcon18';
import StarIcon from '../../../components/statelessComponents/Icons/SkholeIcon12';

const styles = theme => ({
  backToCourseBtn: {
    color: 'inherit',
    fontSize: '2.5rem',
    [theme.breakpoints.down('xs')]: {
      height: '24px',
      width: '24px',
      padding: 0,
    },
  },
});

@injectIntl
@withStyles(s)
@muiWithStyles(styles)
@connect(
  state => ({
    auth: state.auth,
    lesson: state.lessonDetails.lesson,
    loadingChangelog: state.lessonDetails.loadingChangelog,
    course: state.courseDetails.course,
    totalTranscodingJobs: state.transcodingJobs.total,
    isTourOpen: isTourShown(state, 'lessonDetails'),
    offline: state.online.offline,
    review: state.courseReview.review,
  }),
  dispatch =>
    bindActionCreators(
      {
        toggleTour: tourActions.toggleTour,
        getLessonChangelog: lessonActions.getLessonChangelog,
      },
      dispatch,
    ),
)
@withWidth()
export default class LessonHeader extends React.Component {
  static propTypes = {
    lesson: PropTypes.shape({
      _id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      videos: PropTypes.arrayOf(PropTypes.shape({})),
      audios: PropTypes.arrayOf(PropTypes.shape({})),
      youtubeVideoUrl: PropTypes.string,
      backToCourseBtn: PropTypes.shape({}),
    }).isRequired,
    course: PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string,
      slug: PropTypes.string,
    }).isRequired,
    contentTypeSwitchers: PropTypes.shape({
      video: PropTypes.bool.isRequired,
      audio: PropTypes.bool.isRequired,
      text: PropTypes.bool.isRequired,
    }).isRequired,
    switchContentType: PropTypes.func.isRequired,
    totalTranscodingJobs: PropTypes.number.isRequired,
    toggleTour: PropTypes.func.isRequired,
    isTourOpen: PropTypes.bool.isRequired,
    intl: intlShape.isRequired,
    offline: PropTypes.bool.isRequired,
    loadingChangelog: PropTypes.bool.isRequired,
    getLessonChangelog: PropTypes.func.isRequired,
    width: PropTypes.string.isRequired,
    // eslint-disable-next-line react/require-default-props
    review: PropTypes.oneOfType([
      PropTypes.shape({}),
      PropTypes.instanceOf(null),
    ]),
    isTextContentSwitcherDisabled: PropTypes.bool.isRequired,
    classes: PropTypes.shape({
      backToCourseBtn: PropTypes.shape({}),
    }).isRequired,
  };

  state = {
    isMenuOpen: false,
    isChangelogShown: false,
    isCourseReviewShown: false,
  };

  componentWillReceiveProps(nextProps) {
    if (_.get(this, 'props.lesson._id') !== _.get(nextProps, 'lesson._id')) {
      this.handleMenuClose();
    }
  }

  handleMenuOpen = () => {
    this.setState({ isMenuOpen: true });
  };

  handleMenuClose = () => {
    this.setState({ isMenuOpen: false });
  };

  toggleCourseReview = isCourseReviewShown => {
    this.setState({ isCourseReviewShown });
  };

  toggleChangelogModal = isChangelogShown => {
    if (isChangelogShown) {
      this.props.getLessonChangelog(this.props.lesson._id);
    }
    this.setState({
      isChangelogShown,
    });
    this.handleMenuClose();
  };

  renderBack = () => {
    const {
      course,
      intl: { formatMessage },
      classes,
    } = this.props;

    if (!course._id) {
      return (
        <IconButton
          classes={{ root: classes.backToCourseBtn }}
          component={Link}
          to="/"
        >
          <ChevronLeftIcon className={s.backToCourseBtnIcon} />
        </IconButton>
      );
    }

    return (
      <Tooltip
        title={formatMessage(messages.backToTitle, {
          name: course.name,
        })}
      >
        <IconButton
          classes={{ root: classes.backToCourseBtn }}
          component={Link}
          to={`/courses/${course.slug}`}
        >
          <ChevronLeftIcon className={s.backToCourseBtnIcon} />
        </IconButton>
      </Tooltip>
    );
  };

  render() {
    const {
      contentTypeSwitchers,
      lesson,
      course,
      switchContentType,
      totalTranscodingJobs,
      isTourOpen,
      toggleTour,
      intl: { formatMessage },
      offline,
      loadingChangelog,
      width,
      review,
      isTextContentSwitcherDisabled,
    } = this.props;

    const { isMenuOpen, isChangelogShown, isCourseReviewShown } = this.state;
    const showCourseReview =
      isWidthUp('sm', width) &&
      !offline &&
      (_.get(course, 'completedLessons') >= 1 ||
        _.get(course, 'completedQuizzes') >= 1);

    const lessonChangelogElement = togglePopover => (
      <ListItem
        data-test-id="0hE19XyUgAdz3YzM-AgCp"
        onClick={() => {
          this.toggleChangelogModal(true);
          togglePopover(false);
        }}
        button
        dense
      >
        <ListItemText
          primary={formatMessage(messages.changeLogMenuItem)}
          classes={{
            textDense: s.groupMenuItemText,
            root: s.groupMenuItemText,
          }}
        />
      </ListItem>
    );

    return (
      <AppBar className={s.appBar} color="default">
        <Toolbar disableGutters={!isMenuOpen} className={s.toolbar}>
          {this.renderBack()}
          <Typography
            variant="h6"
            component="h1"
            color="inherit"
            className={classNames(s.flex1, s.pageTitle)}
            data-test="lesson-header"
            noWrap
          >
            {lesson.name}
          </Typography>

          <div className={s.flex}>
            <div className={s.flex} id="content-type-switchers">
              {showCourseReview && (
                <Button
                  data-test-id="S-N-1OnQXN69ZEURE4Fid"
                  variant="text"
                  onClick={() => this.toggleCourseReview(true)}
                >
                  <StarIcon className={s.courseReviewIcon} />
                  <Typography variant="subtitle1">
                    {review ? (
                      <FormattedMessage {...messages.editCourseRating} />
                    ) : (
                      <FormattedMessage {...messages.leaveCourseRating} />
                    )}
                  </Typography>
                </Button>
              )}
              <Tooltip title={formatMessage(messages.switchToVideoView)}>
                <Fab
                  data-test-id="JAT8H2FmjXZ-zNYkV6STO"
                  aria-label={formatMessage(messages.switchToVideoView)}
                  className={classNames(s.contentModeButton, {
                    [s.contentModeButtonSelected]:
                      contentTypeSwitchers.video && !offline,
                  })}
                  disabled={
                    offline ||
                    (!_.get(lesson, 'videos.length') &&
                      !totalTranscodingJobs &&
                      !lesson.youtubeVideoUrl)
                  }
                  onClick={() => switchContentType('video')}
                  size="small"
                >
                  <SkholeIcon18
                    defaultColor="#fff"
                    className={s.videoMode}
                    color={
                      contentTypeSwitchers.video && !offline
                        ? '#fff'
                        : 'primary'
                    }
                    disabled={
                      offline ||
                      (!_.get(lesson, 'videos.length') &&
                        !totalTranscodingJobs &&
                        !lesson.youtubeVideoUrl)
                    }
                  />
                </Fab>
              </Tooltip>
              <Tooltip title={formatMessage(messages.switchToAudioView)}>
                <Fab
                  data-test-id="vJO-3eLlGGnJdZo7WRt6o"
                  aria-label={formatMessage(messages.switchToAudioView)}
                  className={classNames(s.contentModeButton, {
                    [s.contentModeButtonSelected]:
                      contentTypeSwitchers.audio && !offline,
                  })}
                  disabled={offline || !_.get(lesson, 'audios.length')}
                  onClick={() => switchContentType('audio')}
                  size="small"
                >
                  <SkholeIcon19
                    disabled={offline || !_.get(lesson, 'audios.length')}
                    defaultColor="#fff"
                    className={s.audioMode}
                    color={
                      contentTypeSwitchers.audio && !offline
                        ? '#fff'
                        : 'primary'
                    }
                  />
                </Fab>
              </Tooltip>
              <Tooltip title={formatMessage(messages.switchToTextView)}>
                <Fab
                  data-test-id="QKRe_Cc_j3bUkrY4Pj9oo"
                  aria-label={formatMessage(messages.switchToTextView)}
                  className={classNames(s.contentModeButton, {
                    [s.contentModeButtonSelected]:
                      contentTypeSwitchers.text || offline,
                  })}
                  onClick={() => switchContentType('text')}
                  disabled={
                    offline ||
                    isTextContentSwitcherDisabled ||
                    (!_.get(lesson, 'audios.length') &&
                      !_.get(lesson, 'videos.length') &&
                      !totalTranscodingJobs &&
                      !lesson.youtubeVideoUrl)
                  }
                  size="small"
                >
                  <SkholeIcon20
                    defaultColor="#fff"
                    color={
                      contentTypeSwitchers.text || offline ? '#fff' : 'primary'
                    }
                    className={s.textMode}
                    disabled={
                      offline ||
                      isTextContentSwitcherDisabled ||
                      (!_.get(lesson, 'audios.length') &&
                        !_.get(lesson, 'videos.length') &&
                        !totalTranscodingJobs &&
                        !lesson.youtubeVideoUrl)
                    }
                  />
                </Fab>
              </Tooltip>
            </div>
            <HelpButton
              tour="lessonDetails"
              className={s.helpIcon}
              lessonChangelogElement={lessonChangelogElement}
              inverted
            />
            <Tour
              update={objectHash({
                courseId: course._id,
                contentTypeSwitchers,
              })}
              steps={[
                {
                  selector: '#content-type-switchers',
                  content: formatMessage(tourMessages.contentTypeSwitchers),
                },
                ...(course._id
                  ? [
                      {
                        selector: '#lesson-navigation-button',
                        content: formatMessage(tourMessages.navigationButton),
                      },
                    ]
                  : []),
                ...(contentTypeSwitchers.text &&
                course._id &&
                !isMobileUserAgent()
                  ? [
                      {
                        selector: '.md-block-paragraph',
                        content: formatMessage(tourMessages.highlight),
                      },
                    ]
                  : []),
                ...(contentTypeSwitchers.video && course._id
                  ? [
                      {
                        selector: '#add-video-note-button',
                        content: formatMessage(tourMessages.videoNote),
                      },
                    ]
                  : []),
                ...(course._id
                  ? [
                      {
                        selector: isWidthUp('sm', width)
                          ? '[data-tour="complete-lesson"]'
                          : '[data-tour="complete-lesson-mobile"]',
                        content: formatMessage(tourMessages.completeButton),
                      },
                    ]
                  : []),
              ]}
              isOpen={isTourOpen}
              onRequestClose={() => toggleTour('lessonDetails', false)}
              className={s.tour}
            />
            {course._id && (
              <Tooltip title={formatMessage(messages.course)}>
                <IconButton
                  data-test-id="lNOSlBzggQHH2_NX68zJB"
                  aria-label={formatMessage(messages.course)}
                  color="inherit"
                  onClick={
                    isMenuOpen ? this.handleMenuClose : this.handleMenuOpen
                  }
                  buttonRef={node => {
                    this.menuAnchorEl = node;
                  }}
                  classes={{
                    root: s.menuButton,
                  }}
                  id="lesson-navigation-button"
                >
                  <SkholeIcon36 className={s.menuIcon} />
                </IconButton>
              </Tooltip>
            )}
          </div>
        </Toolbar>
        <CustomModal
          isOpen={isChangelogShown}
          onClose={() => this.toggleChangelogModal(false)}
          isLoaderOn={loadingChangelog}
        >
          <LessonChangelog
            onClose={() => this.toggleChangelogModal(false)}
            lesson={lesson}
          />
        </CustomModal>
        <CustomModal isOpen={isCourseReviewShown} width="550px">
          <CourseReviewForm
            handleClose={() => this.toggleCourseReview(false)}
          />
        </CustomModal>
        {this.menuAnchorEl && course._id && (
          <SectionItemNavigationMenu
            isMenuOpen={isMenuOpen}
            menuAnchorEl={this.menuAnchorEl}
            handleMenuClose={this.handleMenuClose}
            course={course}
            currentItemId={lesson._id}
          />
        )}
      </AppBar>
    );
  }
}
